/**
 * Firebase Configuration and Authentication
 *
 * DEVELOPMENT MODE: This file is configured to work without Firebase credentials
 * for development and testing purposes.
 *
 * To enable real Firebase authentication:
 * 1. Go to https://console.firebase.google.com/
 * 2. Create a new project or select existing one
 * 3. Enable Authentication > Sign-in method > Google
 * 4. Get your Firebase config from Project Settings > General
 * 5. Set environment variables in .env file:
 *    VITE_FIREBASE_API_KEY=your-api-key
 *    VITE_FIREBASE_AUTH_DOMAIN=your-auth-domain
 *    VITE_FIREBASE_PROJECT_ID=your-project-id
 *    VITE_FIREBASE_STORAGE_BUCKET=your-storage-bucket
 *    VITE_FIREBASE_MESSAGING_SENDER_ID=your-sender-id
 *    VITE_FIREBASE_APP_ID=your-app-id
 *    VITE_USE_MOCK_AUTH=false
 */

import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from "firebase/auth";

import type { User as FirebaseUser } from "firebase/auth";

// Check if we should use mock authentication (for development)
const USE_MOCK_AUTH = false;

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyClfjyZ6mAfi6n8IvMXSbq-myaUEdVRUO0",
  authDomain: "diligent-99ebd.firebaseapp.com",
  projectId: "diligent-99ebd",
  storageBucket: "diligent-99ebd.firebasestorage.app",
  messagingSenderId: "920204626080",
  appId: "1:920204626080:web:3c35312d05a1a4493361c5",
};

// Initialize Firebase only if not using mock auth
let app: any;
let auth: any;
let googleProvider: any;
let db: any;
let storage: any;

if (!USE_MOCK_AUTH) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  googleProvider = new GoogleAuthProvider();
  googleProvider.addScope("profile");
  googleProvider.addScope("email");
}

// Mock authentication for development
let mockUser: FirebaseUser | null = null;
const mockAuthListeners: Array<
  (user: FirebaseUser | null) => void
> = [];

const createMockUser = (
  email: string,
  displayName: string,
): FirebaseUser => {
  return {
    uid: `mock-${email.replace(/[^a-z0-9]/gi, "-")}`,
    email,
    displayName,
    photoURL: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`,
    emailVerified: true,
    isAnonymous: false,
    metadata: {
      creationTime: new Date().toISOString(),
      lastSignInTime: new Date().toISOString(),
    },
    providerData: [
      {
        providerId: "google.com",
        uid: email,
        displayName,
        email,
        photoURL: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`,
        phoneNumber: null,
      },
    ],
    refreshToken: "",
    tenantId: null,
    delete: async () => {},
    getIdToken: async () => "mock-token",
    getIdTokenResult: async () => ({}) as any,
    reload: async () => {},
    toJSON: () => ({}),
    providerId: "firebase",
    phoneNumber: null,
  } as FirebaseUser;
};

/**
 * Sign in with Google using popup
 * @returns Promise with user credentials
 */
export const signInWithGoogle = async () => {
  if (USE_MOCK_AUTH) {
    console.log("[Mock Auth] Simulating Google sign-in...");

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Create a mock user
    const mockEmail = "demo@intellesphere.com";
    const mockDisplayName = "Demo User";

    mockUser = createMockUser(mockEmail, mockDisplayName);

    // Notify all listeners
    mockAuthListeners.forEach((listener) => listener(mockUser));

    console.log(
      "[Mock Auth] Sign-in successful:",
      mockUser.email,
    );

    return {
      user: mockUser,
      providerId: "google.com",
      operationType: "signIn",
    };
  }

  try {
    const result = await signInWithPopup(auth, googleProvider);
    console.log("[Firebase Auth] Sign-in successful:", result);
    return result;
  } catch (error: any) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  if (USE_MOCK_AUTH) {
    console.log("[Mock Auth] Signing out...");
    mockUser = null;
    mockAuthListeners.forEach((listener) => listener(null));
    return;
  }

  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

/**
 * Listen to authentication state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export const onAuthStateChange = (
  callback: (user: FirebaseUser | null) => void,
) => {
  if (USE_MOCK_AUTH) {
    mockAuthListeners.push(callback);
    // Immediately call with current mock user state
    setTimeout(() => callback(mockUser), 0);

    return () => {
      const index = mockAuthListeners.indexOf(callback);
      if (index > -1) {
        mockAuthListeners.splice(index, 1);
      }
    };
  }

  return onAuthStateChanged(auth, callback);
};

/**
 * Get the current authenticated user
 * @returns Current Firebase user or null
 */
export const getCurrentUser = () => {
  if (USE_MOCK_AUTH) {
    return mockUser;
  }
  return auth?.currentUser || null;
};

// Log authentication mode
if (USE_MOCK_AUTH) {
  console.log(
    "[Auth] Running in MOCK authentication mode (no Firebase credentials required)",
  );
  console.log(
    "[Auth] To use real Firebase, set environment variables and VITE_USE_MOCK_AUTH=false",
  );
} else {
  console.log("[Auth] Running with Firebase authentication");
}

export { auth };
export { db };
export { storage };